// React
import { useState } from "react"

// UI
import { PageTemplate } from "@/layouts/PageTemplate"
import { InvestmentOpportunityBanner } from "@/components/banners/InvestmentOpportunityBanner"

// Specific UI
import { ProjectsOverviewTable } from "./_components/ProjectsOverview"
import { FundedAmountPieChart } from "./_components/FundedAmountPieChart"
import { FinancialPerformanceGraph } from "./_components/FinancialPerformanceGraph"
import { ProjectTypeCrowdfunding } from "./_components/ProjectTypeCrowdfunding"
import { ProjectTypeSolarFundsInfo } from "./_components/ProjectTypeSolarFundsInfo"
import { ProjectTypeSolarLeaseInfo } from "./_components/ProjectTypeSolarLeaseInfo"
import { ProjectOwnersOverview } from "./_components/ProjectOwnersOverview"

// Translations
import { useTrans } from "@/i18n"
import { useForceDefaultLanguage } from "@/hooks/useForceDefaultLanguage"

// Context
import { useAuth, AuthStateStatusEnum } from "@/context/auth"

// Types
import {
	ProjectTypeEnum,
	UsersUserProfileLanguageChoices,
} from "@/api/graphql/types"

// Constants
import { ProjectCrowdfundingTypes } from "@/constants/constants"

/**
 * StatisticsPlatform
 * @param param0
 * @returns
 */
export function StatisticsPlatform() {
	// Context
	const auth = useAuth()

	// Translations
	const { DefaultLanguageDisclaimerComponent } = useForceDefaultLanguage(
		UsersUserProfileLanguageChoices.En,
	)
	const t = useTrans(["dashboard", "investments"])

	// State for managing expanded charts
	const [selectedProjectType, setSelectedProjectType] =
		useState<ProjectTypeEnum | null>(null)

	return (
		<>
			<title>
				{t("common:common.web.title", {
					page: t(
						"common:common.navigation.statistics.platform.link",
					),
				})}
			</title>

			<PageTemplate>
				{/** If logged in, show investment opportunities */}
				{auth.status === AuthStateStatusEnum.LOGGED_IN ? (
					<InvestmentOpportunityBanner className="mb-4" />
				) : null}

				<DefaultLanguageDisclaimerComponent />

				{/* Funded Amount Pie Chart */}
				<FundedAmountPieChart
					setSelectedProjectType={setSelectedProjectType}
				/>

				{ProjectCrowdfundingTypes.includes(
					selectedProjectType as ProjectTypeEnum,
				) ? (
					<ProjectTypeCrowdfunding
						onClose={() => setSelectedProjectType(null)}
					/>
				) : null}

				{selectedProjectType === ProjectTypeEnum.SolarFunds ? (
					<ProjectTypeSolarFundsInfo
						onClose={() => setSelectedProjectType(null)}
					/>
				) : null}

				{selectedProjectType === ProjectTypeEnum.SolarLease ? (
					<ProjectTypeSolarLeaseInfo
						onClose={() => setSelectedProjectType(null)}
					/>
				) : null}

				{/** Crowdfunding projects 'SOLAR_SHARES' */}
				<>
					<ProjectsOverviewTable />

					<FinancialPerformanceGraph />

					<ProjectOwnersOverview />
				</>
			</PageTemplate>
		</>
	)
}
